import { createApp, defineAsyncComponent } from 'vue';
import App from './App.vue';
import router from './router';

// Namespaces para exponer como globales a la lib UMD del POS (deps external).
import * as VueLib from 'vue';
import * as PiniaLib from 'pinia';
import * as VueRouterLib from 'vue-router';

// Tipografías de marca, self-hosted (no dependen de Google Fonts).
// Antes las variables declaraban Poppins/Inter pero no se cargaba
// ninguna: todo caía al fallback del sistema.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

// PrimeVue Configuration
import PrimeVue from 'primevue/config';

// ESCALITY Variables Centralizadas (colores, tipografías y escalas).
// Van primero porque el tema de abajo las consume.
import './assets/css/variables-escality.css';

// Tema de PrimeVue con la paleta primaria tokenizada (npm run theme:build).
// Reemplaza a 'primevue/resources/themes/lara-light-blue/theme.css', que
// traía el azul hardcodeado e ignoraba el color de marca.
import './assets/css/primevue-theme.generated.css';

import 'primevue/resources/primevue.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

// Reset y bases tipográficas
import './assets/css/base.css';
// Estilos unificados para filtros de tablas
import './assets/css/table-filters.css';

// Import componentes PrimeVue
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Checkbox from 'primevue/checkbox';
import Calendar from 'primevue/calendar';
import MultiSelect from 'primevue/multiselect';
import Dropdown from 'primevue/dropdown';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputMask from 'primevue/inputmask';
import ColorPicker from 'primevue/colorpicker';
import FileUpload from 'primevue/fileupload';
import Badge from 'primevue/badge';
import Chip from 'primevue/chip';
import Divider from 'primevue/divider';
import Avatar from 'primevue/avatar';
import Toolbar from 'primevue/toolbar';
import OverlayPanel from 'primevue/overlaypanel';
import Menu from 'primevue/menu';
import Card from 'primevue/card';
import Sidebar from 'primevue/sidebar';
import ProgressSpinner from 'primevue/progressspinner';
import SplitButton from 'primevue/splitbutton';
import Message from 'primevue/message';
import InputNumber from 'primevue/inputnumber';
import Dialog from 'primevue/dialog';
import Toast from 'primevue/toast';
import Textarea from 'primevue/textarea';
import RadioButton from 'primevue/radiobutton';
import Panel from 'primevue/panel';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Paginator from 'primevue/paginator';
import ConfirmDialog from 'primevue/confirmdialog';
import Tag from 'primevue/tag';
import InputSwitch from 'primevue/inputswitch';

// Primitivas de layout propias (ver src/components/ui)
import FilterBar from './components/ui/FilterBar.vue';
import TableCard from './components/ui/TableCard.vue';
import EmptyState from './components/ui/EmptyState.vue';
import RowActions from './components/ui/RowActions.vue';
import RowAction from './components/ui/RowAction.vue';
import RowMenu from './components/ui/RowMenu.vue';

// Import PrimeVue directives
import Tooltip from 'primevue/tooltip';

// Import PrimeVue services
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';

import axios from './services/index.js';
window.axios = axios; // para compatibilidad global

// Pinia store (Vue 3 nativo)
import { createPinia } from 'pinia';
import { realtimeSyncPlugin } from './plugins/realtimeSync';

// Laravel Echo para WebSockets
import echo from './plugins/echo';

// Importar store de usuario para permisos
import { useMeStore } from './stores/me';

import moment from 'moment';

// El POS se carga en runtime desde su URL (VITE_APP_POS_URL), no en build-time.
// Mismo código en todos los ambientes; solo cambia la env var. Ver más abajo.

// Sentry configuration
import * as Sentry from '@sentry/vue';

const app = createApp(App);

// Configure PrimeVue
app.use(PrimeVue, {
  ripple: true,
  inputStyle: 'outlined',
});

// Register componentes PrimeVue
app.component('Button', Button);
app.component('InputText', InputText);
app.component('Password', Password);
app.component('Checkbox', Checkbox);
app.component('Calendar', Calendar);
app.component('MultiSelect', MultiSelect);
app.component('Dropdown', Dropdown);
app.component('DataTable', DataTable);
app.component('Column', Column);
app.component('InputMask', InputMask);
app.component('ColorPicker', ColorPicker);
app.component('FileUpload', FileUpload);
app.component('Badge', Badge);
app.component('Chip', Chip);
app.component('Divider', Divider);
app.component('Avatar', Avatar);
app.component('Toolbar', Toolbar);
app.component('OverlayPanel', OverlayPanel);
app.component('Menu', Menu);
app.component('Card', Card);
app.component('Sidebar', Sidebar);
app.component('ProgressSpinner', ProgressSpinner);
app.component('SplitButton', SplitButton);
app.component('Message', Message);
app.component('InputNumber', InputNumber);
app.component('Dialog', Dialog);
app.component('Toast', Toast);
app.component('Textarea', Textarea);
app.component('RadioButton', RadioButton);
app.component('Panel', Panel);
app.component('TabView', TabView);
app.component('TabPanel', TabPanel);
app.component('Paginator', Paginator);
app.component('ConfirmDialog', ConfirmDialog);
app.component('Tag', Tag);
app.component('InputSwitch', InputSwitch);

// Primitivas propias de layout (src/components/ui).
// Globales como las de PrimeVue: se usan en muchas vistas y así migrar
// una pantalla es solo cambiar su template.
app.component('FilterBar', FilterBar);
app.component('TableCard', TableCard);
app.component('EmptyState', EmptyState);
app.component('RowActions', RowActions);
app.component('RowAction', RowAction);
app.component('RowMenu', RowMenu);

// Register PrimeVue directives
app.directive('tooltip', Tooltip);

// Register custom v-can directive para permisos
app.directive('can', {
  mounted(el, binding) {
    const permission = binding.value;
    const modifiers = binding.modifiers;
    const bypass = true; // BYPASS_PERMISSIONS activo

    if (bypass) {
      // Con bypass activo, siempre mostrar
      return;
    }

    const meStore = useMeStore();
    const user = meStore.getUser;
    const permissions = user && user.permissions ? user.permissions : [];

    const hasPermission = permissions.includes(permission);

    if (!hasPermission) {
      if (modifiers.hide) {
        el.style.display = 'none';
      } else if (modifiers.disable) {
        el.disabled = true;
        el.style.opacity = '0.5';
        el.style.cursor = 'not-allowed';
      } else {
        el.style.display = 'none'; // Por defecto ocultar
      }
    }
  },
  updated(el, binding) {
    // Re-evaluar permisos si cambian
    const permission = binding.value;
    const modifiers = binding.modifiers;
    const bypass = true; // BYPASS_PERMISSIONS activo

    if (bypass) {
      el.style.display = '';
      el.disabled = false;
      el.style.opacity = '';
      el.style.cursor = '';
      return;
    }

    const meStore = useMeStore();
    const user = meStore.getUser;
    const permissions = user && user.permissions ? user.permissions : [];

    const hasPermission = permissions.includes(permission);

    if (hasPermission) {
      el.style.display = '';
      el.disabled = false;
      el.style.opacity = '';
      el.style.cursor = '';
    } else {
      if (modifiers.hide) {
        el.style.display = 'none';
      } else if (modifiers.disable) {
        el.disabled = true;
        el.style.opacity = '0.5';
        el.style.cursor = 'not-allowed';
      } else {
        el.style.display = 'none';
      }
    }
  },
});

// Register custom v-cannot directive para permisos (opuesto a v-can)
app.directive('cannot', {
  mounted(el, binding) {
    const permission = binding.value;
    const bypass = true; // BYPASS_PERMISSIONS activo

    if (bypass) {
      // Con bypass activo, siempre ocultar (simular que NO tiene el permiso)
      el.style.display = 'none';
      return;
    }

    const meStore = useMeStore();
    const user = meStore.getUser;
    const permissions = user && user.permissions ? user.permissions : [];

    const hasPermission = permissions.includes(permission);

    if (hasPermission) {
      // Si TIENE el permiso, ocultar (porque es v-cannot)
      el.style.display = 'none';
    } else {
      // Si NO tiene el permiso, mostrar
      el.style.display = '';
    }
  },
  updated(el, binding) {
    // Re-evaluar permisos si cambian
    const permission = binding.value;
    const bypass = true; // BYPASS_PERMISSIONS activo

    if (bypass) {
      el.style.display = 'none';
      return;
    }

    const meStore = useMeStore();
    const user = meStore.getUser;
    const permissions = user && user.permissions ? user.permissions : [];

    const hasPermission = permissions.includes(permission);

    if (hasPermission) {
      el.style.display = 'none';
    } else {
      el.style.display = '';
    }
  },
});

// Register PrimeVue services
app.use(ConfirmationService);
app.use(ToastService);

// Use chart.js (compatibility)

// Use router
app.use(router);

// Use Pinia con plugin de real-time sync
const pinia = createPinia();
pinia.use(realtimeSyncPlugin); // ✨ Plugin de real-time automático
app.use(pinia);

// Exponer las deps compartidas como globales para la lib UMD del POS
// (marcadas como external en su build:lib → las toma del host).
window.Vue = VueLib;
window.Pinia = PiniaLib;
window.VueRouter = VueRouterLib;
window.PrimeVue = PrimeVue;

// Registrar <escality-pos> como componente async que carga el POS compilado
// desde su propia URL en runtime (mismo código en local/staging/prod; cambia
// solo VITE_APP_POS_URL, ej: https://pos-stg.escality.online).
const EscalityPOS = defineAsyncComponent(() => new Promise((resolve, reject) => {
  const base = import.meta.env.VITE_APP_POS_URL;
  if (!base) {
    reject(new Error('VITE_APP_POS_URL no configurada'));
    return;
  }
  if (window.EscalityPOS) {
    resolve(window.EscalityPOS.NuevaVenta || window.EscalityPOS.default || window.EscalityPOS);
    return;
  }
  const s = document.createElement('script');
  s.src = `${base.replace(/\/$/, '')}/lib/pos.umd.js`;
  s.onload = () => {
    const mod = window.EscalityPOS;
    const comp = mod && (mod.NuevaVenta || mod.default || mod);
    comp ? resolve(comp) : reject(new Error('POS lib cargó pero no expone el componente'));
  };
  s.onerror = () => reject(new Error('No se pudo cargar el POS desde ' + s.src));
  document.head.appendChild(s);
}));
app.component('escality-pos', EscalityPOS);

// Global properties (Vue 3 way)
app.config.globalProperties.$http = axios;
app.config.globalProperties.moment = moment;
app.config.globalProperties.$echo = echo; // ✨ Laravel Echo disponible globalmente

// 🔐 CONFIGURACIÓN DE PERMISOS (adaptado para Vue 3)
const BYPASS_PERMISSIONS = true;

app.config.globalProperties.$can = function (permission) {
  if (BYPASS_PERMISSIONS) {
    console.log(
      '🔓 Bypass de permisos activo - Permiso concedido:',
      permission
    );
    return true;
  }
  const meStore = useMeStore();
  const user = meStore.getUser;
  const permissions = user && user.permissions ? user.permissions : [];
  return permissions.includes(permission);
};

app.config.globalProperties.$canAny = function (permissionsList) {
  if (BYPASS_PERMISSIONS) return true;
  return permissionsList.some(permission => this.$can(permission));
};

app.config.globalProperties.$canAll = function (permissionsList) {
  if (BYPASS_PERMISSIONS) return true;
  return permissionsList.every(permission => this.$can(permission));
};

app.config.globalProperties.$cannot = function (permission) {
  if (BYPASS_PERMISSIONS) return false;
  return !this.$can(permission);
};

app.config.globalProperties.$refreshPermissions = async function () {
  const meStore = useMeStore();
  const user = meStore.getUser;
  return Promise.resolve(user.permissions || []);
};

app.config.globalProperties.$hasRole = function (role) {
  if (BYPASS_PERMISSIONS) return true;
  const meStore = useMeStore();
  const user = meStore.getUser;
  return user && user.role && user.role.name === role;
};

// Error tracking. Se activa en cualquier entorno que tenga DSN configurado:
// en desarrollo apunta al GlitchTip self-hosted y en producción a Sentry SaaS.
// Antes estaba limitado a producción, así que en local no se reportaba nada.
const sentryDsn = import.meta.env.VITE_APP_SENTRY_DSN;

if (sentryDsn) {
  const isProd = import.meta.env.PROD;

  Sentry.init({
    app,
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay es exclusivo de Sentry SaaS: GlitchTip no implementa
      // el endpoint y los envíos quedarían rebotando en desarrollo.
      ...(isProd ? [Sentry.replayIntegration()] : []),
    ],
    tracesSampleRate:
      parseFloat(import.meta.env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE) || 1.0,
    tracePropagationTargets: ['localhost', 'api.escality.local', /^https:\/\/api\.escality\.online/],
    ...(isProd
      ? { replaysSessionSampleRate: 0.1, replaysOnErrorSampleRate: 1.0 }
      : {}),
  });
}

app.mount('#app');
